import { action, computed, observable, makeAutoObservable } from 'mobx';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import {
    validateHexColor,
    validateNotEmpty,
    convertToNumber,
    validateSlug,
    convertToNumberArray,
} from 'src_common/common/mobx-utils/Form2/validators';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import { SportsDataModelType } from 'src/api/config/sports/getAllCustomizedSports';
import { UploadImageState } from 'src/AppState/UploadImageState/UploadImageState';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';
import { arrayMove } from 'react-movable';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import { SdkStaff, SportsListType } from 'src/AppState/sdk/staff';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { ThemeColorsType } from 'src/domains/common/themeColors';

const SportsFilterIO = t.interface({
    slug: t.string,
    label: t.string,
    displayOrder: t.number,
    competitionIds: t.array(t.number),
});
export type SportsFilterType = t.TypeOf<typeof SportsFilterIO>;
export const isSportFilter = createGuard(SportsFilterIO);

const SportsDetailsIO = t.interface({
    competitionIds: t.array(t.number),
    filters: t.array(SportsFilterIO),
});
export type SportsDetailsType = t.TypeOf<typeof SportsDetailsIO>;
const isSportsDetails = createGuard(SportsDetailsIO);

type FormType = {
    sport_slug: string;
    sport_label?: null | undefined | string;
    sport_label_color?: null | undefined | string;
    header_bg_color?: null | undefined | string;
    sport_description?: null | undefined | string;
    sport_description_text_color?: null | undefined | string;

    header_img_big?: null | undefined | string;
    header_img_medium?: null | undefined | string;
    header_img_small?: null | undefined | string;
    is_active_header_image: boolean;

    scoreboard_inplay_bg_color?: null | undefined | string;
    scoreboard_inplay_text_color?: null | undefined | string;
    scoreboard_inplay_img_big?: null | undefined | string;
    scoreboard_inplay_img_medium?: null | undefined | string;
    scoreboard_inplay_img_small?: null | undefined | string;
    is_active_inplay_image: boolean;

    scoreboard_prematch_bg_color?: null | undefined | string;
    scoreboard_prematch_text_color?: null | undefined | string;
    scoreboard_prematch_img_big?: null | undefined | string;
    scoreboard_prematch_img_medium?: null | undefined | string;
    scoreboard_prematch_img_small?: null | undefined | string;
    is_active_prematch_image: boolean;
};

interface OpenSectionsType {
    sportHeader: boolean;
    scoreboardPreMatch: boolean;
    scoreboardInPlay: boolean;
    colorPicker: boolean;
    imageUpload: boolean;
    customFilters: boolean;
}

export type KeyOfOpenSectionsType = keyof OpenSectionsType;

export interface SportListOptionType {
    value: string;
    label: string;
}

type SportTypeType = 'existing-sport' | 'new-sport';

export class CustomizedSportsState {
    @observable private readonly sportsDataResource: Resource<Array<SportsDataModelType>>;
    @observable private readonly allSports: Resource<SportsListType>;
    @observable public isEditMode: boolean = false;
    @observable private singleSport: SportsDataModelType | null = null;
    @observable public errorMessageForList: string | null = null;
    @observable public imagesError: string | null = null;
    @observable public isOpenSections: OpenSectionsType = {
        sportHeader: true,
        scoreboardPreMatch: false,
        scoreboardInPlay: false,
        colorPicker: true,
        imageUpload: false,
        customFilters: false,
    };
    @observable public sportConfigType: SportTypeType = 'existing-sport';
    @observable public filtersMap: Map<string, SportsFilterType>;
    @observable public activeFilter: string | null = null;
    @observable public isActiveSport: boolean = false;

    public selectedValue: FormInputState<string, string>;

    public isActiveHeaderImage: FormInputState<boolean, boolean>;
    public sportSlug: FormInputState<string, string>;
    public sportLabel: FormInputState<string, string>;
    public sportLabelColor: FormInputState<string, string>;
    public sportHeaderBgColor: FormInputState<string, string>;
    public sportDescription: FormInputState<string, string>;
    public sportDescriptionTxtColor: FormInputState<string, string>;

    public headerImageDesktop: UploadImageState;
    public headerImageTablet: UploadImageState;
    public headerImageMobile: UploadImageState;

    public isActiveInPlayImage: FormInputState<boolean, boolean>;
    public scoreboardInPlayTxtColor: FormInputState<string, string>;
    public scoreboardInPlayBgColor: FormInputState<string, string>;
    public scoreboardInPlayImageDesktop: UploadImageState;
    public scoreboardInPlayImageTablet: UploadImageState;
    public scoreboardInPlayImageMobile: UploadImageState;

    public isActivePreMatchImage: FormInputState<boolean, boolean>;
    public scoreboardPreMatchTxtColor: FormInputState<string, string>;
    public scoreboardPreMatchBgColor: FormInputState<string, string>;
    public scoreboardPreMatchImageDesktop: UploadImageState;
    public scoreboardPreMatchImageTablet: UploadImageState;
    public scoreboardPreMatchImageMobile: UploadImageState;

    public filterName: FormInputState<string, string>;
    public competitionIdInput: FormInputState<string, number>;
    public competitionIdsInput: FormInputState<string, Array<number>>;

    private readonly form: FormModel<FormType>;

    private readonly noEmptyValidationMessage = 'This field is required.';

    public constructor(
        private readonly modelsState: ModelsState,
        private readonly sdkStaff: SdkStaff,
        private readonly theme: ThemeColorsType,
        private readonly trpcClient: TrpcClient
    ) {
        this.sportsDataResource = new Resource(async (): Promise<Array<SportsDataModelType>> => {
            const response = await this.trpcClient.client.customSportsRouter.getAllCustomSports.query();

            // @ts-expect-error LT-2197 - remove when task is done
            return response;
        });

        this.selectedValue = FormInputState.new('Select sport');
        this.isActiveHeaderImage = FormInputState.new<boolean>(true);
        this.sportSlug = FormInputState.new('')
            .map(validateNotEmpty(this.noEmptyValidationMessage))
            .map(validateSlug('Slug not valid. You can use mix of A-Z, a-z, 0-9, -. Example: special-spport-123'));
        this.sportLabel = FormInputState.new('');
        this.sportLabelColor = FormInputState.new(this.theme.colorPicker.txtDefault).map(
            validateHexColor('Hex color value should be 7 characters long (including #).')
        );
        this.sportHeaderBgColor = FormInputState.new(this.theme.colorPicker.bgDefault).map(
            validateHexColor('Hex color value should be 7 characters long (including #).')
        );
        this.sportDescription = FormInputState.new('');
        this.sportDescriptionTxtColor = FormInputState.new('').map(
            validateHexColor('Hex color value should be 7 characters long (including #).')
        );

        this.headerImageDesktop = new UploadImageState('sport/desktop_image');
        this.headerImageTablet = new UploadImageState('sport/tablet_image');
        this.headerImageMobile = new UploadImageState('sport/mobile_image');

        this.isActiveInPlayImage = FormInputState.new<boolean>(true);
        this.scoreboardInPlayTxtColor = FormInputState.new('').map(
            validateHexColor('Hex color value should be 7 characters long (including #).')
        );
        this.scoreboardInPlayBgColor = FormInputState.new('').map(
            validateHexColor('Hex color value should be 7 characters long (including #).')
        );
        this.scoreboardInPlayImageDesktop = new UploadImageState('sport/desktop_image');
        this.scoreboardInPlayImageTablet = new UploadImageState('sport/tablet_image');
        this.scoreboardInPlayImageMobile = new UploadImageState('sport/mobile_image');

        this.isActivePreMatchImage = FormInputState.new<boolean>(true);
        this.scoreboardPreMatchTxtColor = FormInputState.new('').map(
            validateHexColor('Hex color value should be 7 characters long (including #).')
        );
        this.scoreboardPreMatchBgColor = FormInputState.new('').map(
            validateHexColor('Hex color value should be 7 characters long (including #).')
        );
        this.scoreboardPreMatchImageDesktop = new UploadImageState('sport/desktop_image');
        this.scoreboardPreMatchImageTablet = new UploadImageState('sport/tablet_image');
        this.scoreboardPreMatchImageMobile = new UploadImageState('sport/mobile_image');

        this.filterName = FormInputState.new('');
        this.competitionIdInput = FormInputState.new('').map(convertToNumber('Not a number'));
        this.competitionIdsInput = FormInputState.new('').map(convertToNumberArray('One of input is not a number'));
        this.filtersMap = new Map();

        this.allSports = new Resource(async (): Promise<SportsListType> => {
            return await this.sdkStaff.getAllSports();
        });

        this.form = FormModel.group({
            sport_slug: this.sportSlug,
            sport_label: this.sportLabel,
            sport_label_color: this.sportLabelColor,
            header_bg_color: this.sportHeaderBgColor,
            sport_description: this.sportDescription,
            sport_description_txt_color: this.sportDescriptionTxtColor,
            is_active_header_image: this.isActiveHeaderImage,
            scoreboard_inplay_text_color: this.scoreboardInPlayTxtColor,
            scoreboard_inplay_bg_color: this.scoreboardInPlayBgColor,
            is_active_inplay_image: this.isActiveInPlayImage,
            is_active_prematch_image: this.isActivePreMatchImage,
            scoreboard_prematch_text_color: this.scoreboardPreMatchTxtColor,
            scoreboard_prematch_bg_color: this.scoreboardPreMatchBgColor,
        });
        makeAutoObservable(this);
    }

    @computed public get isLoading(): boolean {
        const sportsDetailsData = this.sportsDataResource.get();
        return sportsDetailsData.type === 'loading';
    }

    @computed private get sportList(): SportsListType {
        const allSports = this.allSports.get();
        if (allSports.type === 'ready') {
            return allSports.value;
        }
        return [];
    }

    public get sportListOptions(): Array<SportListOptionType> {
        const sportDetailsDataSlugList = this.sportDetailsData.map((element) => element.sport_slug);
        const availableSportsList = this.sportList.filter(
            (element) => sportDetailsDataSlugList.includes(element.id) === false
        );

        const sportsList = availableSportsList.map((elem) => {
            return {
                value: elem.id,
                label: elem.name,
            };
        });

        return sportsList;
    }

    @action public handleSportSelect = (value: string): void => {
        const selectedSport = this.sportListOptions.find((sport) => sport.value === value);
        this.selectedValue.setValue(selectedSport?.label ?? '');
        this.sportLabel.setValue(this.selectedValue.value);
    };

    @computed public get isSportSelected(): boolean {
        return this.selectedValue.value !== 'Select sport';
    }

    @computed public get sportDetailsData(): Array<SportsDataModelType> {
        const sportDetailsData = this.sportsDataResource.get();
        if (sportDetailsData.type === 'ready') {
            return sportDetailsData.value.sort((a, b) => (a.sport_slug > b.sport_slug ? 1 : -1));
        }
        return [];
    }

    @action public handleToggleOpenSections = (selection: KeyOfOpenSectionsType): void => {
        this.isOpenSections = {
            ...this.isOpenSections,
            [selection]: !this.isOpenSections[selection],
        };
    };

    @action public handleCreate = async (): Promise<void> => {
        this.form.setAsVisited();
        const { result } = this.form;

        if (result.value.type === 'ok') {
            const sportDetailsData = {
                ...result.value.data,

                is_active_header_image:
                    this.sportConfigType === 'new-sport' ? false : result.value.data.is_active_header_image,

                header_img_big: this.headerImageDesktop.imgUrl,
                header_img_medium: this.headerImageTablet.imgUrl,
                header_img_small: this.headerImageMobile.imgUrl,

                scoreboard_prematch_img_big: this.scoreboardPreMatchImageDesktop.imgUrl,
                scoreboard_prematch_img_medium: this.scoreboardPreMatchImageTablet.imgUrl,
                scoreboard_prematch_img_small: this.scoreboardPreMatchImageMobile.imgUrl,

                scoreboard_inplay_img_big: this.scoreboardInPlayImageDesktop.imgUrl,
                scoreboard_inplay_img_medium: this.scoreboardInPlayImageTablet.imgUrl,
                scoreboard_inplay_img_small: this.scoreboardInPlayImageMobile.imgUrl,

                sport_type: this.sportConfigType,
                is_active: this.isActiveSport,
                details: this.prepareDetails,
            };

            try {
                const submitResult = await this.trpcClient.client.customSportsRouter.createCustomSport.mutate({
                    data: sportDetailsData,
                });

                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                if (submitResult === null) {
                    return this.handleError('Something went wrong.');
                }
                await this.sportsDataResource.refresh();
                this.resetForm();
            } catch (error) {
                console.error(error);
            }
        }
    };

    @action public resetColors = (): void => {
        this.setHeaderTextColor(this.theme.colorPicker.txtDefault);
        this.setHeaderBgColor(this.theme.colorPicker.bgDefault);
    };

    @action public setHeaderTextColor = (value: string): void => {
        this.sportLabelColor.setValue(value);
    };

    @action public setHeaderBgColor = (value: string): void => {
        this.sportHeaderBgColor.setValue(value);
    };

    @action public setActiveImg = async (id: number, screenType: string): Promise<void> => {
        const item = this.sportDetailsData.find((item) => item.id === id);
        let params;

        if (item === undefined) {
            console.error('Cannot find the singleSport with provided ID.');
        } else {
            if (screenType === 'header') {
                params = {
                    data: {
                        ...item,
                        is_active_header_image: !item.is_active_header_image,
                    },
                    id: id,
                };
            }

            if (screenType === 'scoreboard-prematch') {
                params = {
                    data: {
                        ...item,
                        is_active_prematch_image: !item.is_active_prematch_image,
                    },
                    id: id,
                };
            }

            if (screenType === 'scoreboard-inplay') {
                params = {
                    data: {
                        ...item,
                        is_active_inplay_image: !item.is_active_inplay_image,
                    },
                    id: id,
                };
            }

            if (params !== undefined) {
                try {
                    await this.trpcClient.client.customSportsRouter.updateCustomSport.mutate(params);
                    await this.sportsDataResource.refresh();
                    this.resetForm();
                } catch (error) {
                    console.error(error);
                }
            }
        }
    };

    @action public setEditMode = (value: boolean): void => {
        this.isEditMode = value;
    };

    @action public setEditSport = async (item: SportsDataModelType): Promise<void> => {
        this.singleSport = item;
        this.selectedValue.setValue(item.sport_slug);
        this.sportSlug.setValue(item.sport_slug);
        this.sportLabel.setValue(item.sport_label ?? '');
        this.sportLabelColor.setValue(item.sport_label_color ?? '');
        this.sportHeaderBgColor.setValue(item.header_bg_color ?? '');
        this.sportDescription.setValue(item.sport_description ?? '');
        this.sportDescriptionTxtColor.setValue(item.sport_description_text_color ?? '');

        console.log('test', item.sport_label_color);

        this.isActiveHeaderImage.setValue(item.is_active_header_image);
        this.headerImageDesktop.handleChooseImage(item.header_img_big ?? '');
        this.headerImageTablet.handleChooseImage(item.header_img_medium ?? '');
        this.headerImageMobile.handleChooseImage(item.header_img_small ?? '');

        this.isActivePreMatchImage.setValue(item.is_active_prematch_image);
        this.scoreboardPreMatchImageDesktop.handleChooseImage(item.scoreboard_prematch_img_big ?? '');
        this.scoreboardPreMatchImageTablet.handleChooseImage(item.scoreboard_prematch_img_medium ?? '');
        this.scoreboardPreMatchImageMobile.handleChooseImage(item.scoreboard_prematch_img_small ?? '');

        this.isActiveInPlayImage.setValue(item.is_active_inplay_image);
        this.scoreboardInPlayImageDesktop.handleChooseImage(item.scoreboard_inplay_img_big ?? '');
        this.scoreboardInPlayImageTablet.handleChooseImage(item.scoreboard_inplay_img_medium ?? '');
        this.scoreboardInPlayImageMobile.handleChooseImage(item.scoreboard_inplay_img_small ?? '');

        this.sportConfigType = item.sport_type === 'new-sport' ? 'new-sport' : 'existing-sport';
        this.isActiveSport = item.is_active;

        if (isSportsDetails(item.details)) {
            this.competitionIdsInput.setValue(item.details.competitionIds.toString());
            this.filtersMap = new Map();
            for (const filter of item.details.filters) {
                this.filtersMap.set(filter.slug, filter);
            }
        }
    };

    @action public handleEdit = async (): Promise<void> => {
        this.form.setAsVisited();
        const result = this.form.result;

        if (result.value.type === 'ok' && this.singleSport !== null) {
            const sportsDetailsData = {
                ...result.value.data,

                header_img_big: this.headerImageDesktop.imgUrl,
                header_img_medium: this.headerImageTablet.imgUrl,
                header_img_small: this.headerImageMobile.imgUrl,

                scoreboard_prematch_img_big: this.scoreboardPreMatchImageDesktop.imgUrl,
                scoreboard_prematch_img_medium: this.scoreboardPreMatchImageTablet.imgUrl,
                scoreboard_prematch_img_small: this.scoreboardPreMatchImageMobile.imgUrl,

                scoreboard_inplay_img_big: this.scoreboardInPlayImageDesktop.imgUrl,
                scoreboard_inplay_img_medium: this.scoreboardInPlayImageTablet.imgUrl,
                scoreboard_inplay_img_small: this.scoreboardInPlayImageMobile.imgUrl,

                sport_type: this.sportConfigType,
                is_active: this.isActiveSport,
                details: this.prepareDetails,
            };

            const submitResult = await this.trpcClient.client.customSportsRouter.updateCustomSport.mutate({
                data: sportsDetailsData,
                id: this.singleSport.id,
            });
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (submitResult === null) {
                console.error('Error');
            } else {
                await this.sportsDataResource.refresh();
                this.resetForm();
            }
        }
    };

    @action public resetForm = (): void => {
        this.form.reset();
        this.isEditMode = false;
        this.singleSport = null;

        this.headerImageDesktop.handleResetImage();
        this.headerImageTablet.handleResetImage();
        this.headerImageMobile.handleResetImage();

        this.scoreboardPreMatchImageDesktop.handleResetImage();
        this.scoreboardPreMatchImageTablet.handleResetImage();
        this.scoreboardPreMatchImageMobile.handleResetImage();

        this.scoreboardInPlayImageDesktop.handleResetImage();
        this.scoreboardInPlayImageTablet.handleResetImage();
        this.scoreboardInPlayImageMobile.handleResetImage();

        this.selectedValue.setValue('Select sport');

        this.sportConfigType = 'existing-sport';
        this.isActiveSport = false;
        this.competitionIdsInput.reset();
    };

    @action public handleResetForm = async (): Promise<void> => {
        if (this.isEditMode && this.singleSport !== null) {
            await this.setEditSport(this.singleSport);
        } else {
            this.resetForm();
        }
    };

    @action public deleteSport = async (id: number): Promise<void> => {
        const removeResult = await this.trpcClient.client.customSportsRouter.deleteCustomSport.mutate({ id });
        if (removeResult.status === 200) {
            await this.sportsDataResource.refresh();
        } else {
            console.error("The item couldn't be deleted.");
        }
    };

    @action private handleError = async (message: string): Promise<void> => {
        this.errorMessageForList = message;
        await timeout(5000);
        this.errorMessageForList = null;
    };

    @action public handleSportConfigType = (sportConfigType: SportTypeType): void => {
        if (this.isEditMode === false) {
            this.sportConfigType = sportConfigType;
        }
    };

    @action public handleAddFilter = (): void => {
        const result = this.filterName.result;

        const value = result.value.type === 'ok' ? result.value.data : null;

        if (value === null) {
            return;
        }

        const slug = value.toLowerCase().split(' ').join('-');

        // if (slug === null) {
        //     return;
        // }

        const filterItem = this.filtersMap.get(slug) ?? null;

        if (filterItem !== null) {
            return;
        }

        this.filtersMap.set(slug, {
            slug: slug,
            label: value,
            displayOrder: this.filtersMap.size + 1,
            competitionIds: [],
        });

        this.filterName.reset();
    };

    @action public handleFiltersOrder = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }): void => {
        const filtersSlugList = arrayMove(this.filtersList, oldIndex, newIndex);

        const tempMap = new Map();
        const tempFilters = filtersSlugList.map((filter, index) => ({ ...filter, displayOrder: index + 1 }));
        for (const filterItem of tempFilters) {
            tempMap.set(filterItem.slug, filterItem);
        }

        this.filtersMap = tempMap;
    };

    @computed public get filtersList(): Array<SportsFilterType> {
        return Array.from(this.filtersMap.values());
    }

    @action public onActiveFilter = (slug: string): void => {
        if (this.activeFilter === slug) {
            this.activeFilter = null;
        } else {
            this.activeFilter = slug;
        }

        this.competitionIdInput.reset();
    };

    @action public onRemoveFilter = (slug: string): void => {
        this.filtersMap.delete(slug);

        if (this.activeFilter === slug) {
            this.activeFilter = null;
        }
    };

    @computed public get competitionIdValue(): number | null {
        const result = this.competitionIdInput.result;
        return result.value.type === 'ok' ? result.value.data : null;
    }

    @action public handleAddCompetition = (): void => {
        if (this.competitionIdValue !== null) {
            const competitionModel = this.modelsState.getCompetitionModel(this.competitionIdValue);
            if (competitionModel !== null && this.activeFilter !== null) {
                const filterItem = this.filtersMap.get(this.activeFilter) ?? null;
                if (filterItem !== null) {
                    const competitions = new Set(filterItem.competitionIds);
                    competitions.add(competitionModel.id);

                    this.filtersMap.set(this.activeFilter, {
                        ...filterItem,
                        competitionIds: Array.from(competitions),
                    });

                    this.competitionIdInput.reset();
                }
            }
        }
    };

    @action public handleDeleteCompetition = (slug: string, competitionId: number): void => {
        const filterItem = this.filtersMap.get(slug) ?? null;
        if (filterItem !== null) {
            this.filtersMap.set(slug, {
                ...filterItem,
                competitionIds: filterItem.competitionIds.filter((elem) => elem !== competitionId),
            });

            this.competitionIdInput.reset();
        }
    };

    @computed public get competitionName(): string | null {
        if (this.competitionIdValue !== null) {
            const competitionModel = this.modelsState.getCompetitionModel(this.competitionIdValue);

            if (competitionModel !== null) {
                return competitionModel.name;
            }
        }

        return null;
    }

    @computed private get prepareDetails(): SportsDetailsType {
        const competitionIds =
            this.competitionIdsInput.result.value.type === 'ok' ? this.competitionIdsInput.result.value.data : [];

        return {
            competitionIds,
            filters: this.filtersList,
        };
    }

    @action public onChangeActiveSport = async (isActive: boolean, sportData: SportsDataModelType): Promise<void> => {
        try {
            await this.trpcClient.client.customSportsRouter.updateCustomSport.mutate({
                data: { ...sportData, is_active: !isActive },
                id: sportData.id,
            });
            await this.sportsDataResource.refresh();
            this.resetForm();
        } catch (error) {
            console.error(error);
        }
    };
}
